import {
	getPropertySortText,
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	MultiSelectOption
} from "client/Components";
import { convertSortDirectionToDto, DataSourceSortDirection } from "client/Data/sorting";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createRemoteDataSource,
	LazyLoadCursorKind,
	MediaQueryKind,
	RemoteDataSourceState,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { generateSharePointUniqueKey } from "../../Documents/SharePoint/SharePointOverView";
import {
	GlobalFileSearchDocumentKind,
	GlobalFileSearchResultModel,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchFiles,
	SearchIntegrationKind,
	SharePointOrderModel,
	SharePointSearchTimeScope,
	SharePointSearchValue
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { DocumentItemViewModel, SharePointColumnId } from "client/Api/Documents/SharePoint/common";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import { SearchNoResults } from "../SearchNoResults";
import { TimeZone } from "client/DateAndTime/timezones";
import { checkHasFilter } from "./common";
import { DocumentItemView } from "../Items/DocumentItemView";
import { Guid } from "client_legacy/ndaw/guid";

export type FileSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const FileSearch = (props: FileSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const [formData, setFormData] = useState<FileSearchFormData>({
		...defaultFilters,
		sorting: { direction: DataSourceSortDirection.Ascending, propertyName: "name" }
	});

	const listHandle: RefObject<ScrollableVirtualListHandle<GlobalFileSearchResultModel> | null> = useRef<ScrollableVirtualListHandle<GlobalFileSearchResultModel>>(null);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<FileSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const {
		sorting: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);

	const partialDataSourceOptions: Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> = useValueEqualMemo(
		() => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				documentKinds: formData.fileType,
				searchIn: formData.searchIn,
				timeScope: formData.timeScope
			},
			globalContext: {
				sorting: formData.sorting,
				searchVersion: searchTerm.version
			}
		}),
		[
			formData.fileType,
			formData.searchIn,
			formData.sorting,
			formData.timeScope,
			searchTerm
		]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems,
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	const renderContent = (): ReactElement => {

		const loadingState: RemoteDataSourceState = getLoadingState();

		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={112}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="file-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: GlobalFileSearchResultModel, index: number): ReactElement => (
						<DocumentItemView
							item={item}
							content={item.content}
							navigationIndex={index}
							index={index}
							itemScope={fileSearchScope}
							isMobile={isMobile}
							timezone={userTimezone}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sorting")}
						options={sortOptions}
						isExpandedByDefault
					/>

					<SearchSettings.MultiSelect
						label={localize("_FileType")}
						attachable={formHandler.for("fileType")}
						options={fileTypeOptions}
						placeholder={localize("_SelectFileType(s)")}
					/>

					<SearchSettings.Select
						label={localize("_LastModified")}
						attachable={formHandler.for("timeScope")}
						options={timeScopeOptions}
					/>

					<SearchSettings.Select
						label={localize("_ResultsBasedOn")}
						attachable={formHandler.for("searchIn")}
						options={searchInOptions}
					/>
				</SearchSettings>

			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
FileSearch.displayName = "FileSearch";

function getUniqueKey(item: GlobalFileSearchResultModel): string {
	return generateSharePointUniqueKey(item.document);
}

export const PAGE_SIZE = 20;

type Sortvalue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: StringKeyOf<Pick<DocumentItemViewModel, "name" | "modifiedOn">>;
};

type FileSearchFormData = {
	sorting: Sortvalue;
	fileType: readonly GlobalFileSearchDocumentKind[];
	timeScope: SharePointSearchTimeScope;
	searchIn: SharePointSearchValue;
};

type FilterModel = Omit<FileSearchFormData, "sorting">;

const defaultFilters: FilterModel = {
	fileType: [],
	timeScope: SharePointSearchTimeScope.AnyTime,
	searchIn: SharePointSearchValue.All
};

const sortOptions: readonly SelectOption<Sortvalue>[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	},
	{
		text: getPropertySortText("_DateModified", "_NewToOld"),
		value: { propertyName: "modifiedOn", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "modified-on-new-old"
	},
	{
		text: getPropertySortText("_DateModified", "_OldToNew"),
		value: { propertyName: "modifiedOn", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "modified-on-old-new"
	}
];

const fileTypeOptions: readonly MultiSelectOption<GlobalFileSearchDocumentKind>[] = [
	{
		text: localize("_Word"),
		value: GlobalFileSearchDocumentKind.Word
	},
	{
		text: localize("_Excel"),
		value: GlobalFileSearchDocumentKind.Excel
	},
	{
		text: localize("_PowerPoint"),
		value: GlobalFileSearchDocumentKind.PowerPoint
	},
	{
		text: localize("_OneNote"),
		value: GlobalFileSearchDocumentKind.OneNote
	},
	{
		text: localize("_Visio"),
		value: GlobalFileSearchDocumentKind.Visio
	},
	{
		text: localize("_PDF"),
		value: GlobalFileSearchDocumentKind.PDF
	},
	{
		text: localize("_Photo"),
		value: GlobalFileSearchDocumentKind.Photo
	},
	{
		text: localize("_Video"),
		value: GlobalFileSearchDocumentKind.Video
	},
	{
		text: localize("_Webpage"),
		value: GlobalFileSearchDocumentKind.WebPage
	},
	{
		text: localize("_Other"),
		value: GlobalFileSearchDocumentKind.Other
	},
];

const timeScopeOptions: readonly SelectOption<SharePointSearchTimeScope>[] = [
	{
		value: SharePointSearchTimeScope.AnyTime,
		text: localize("_AnyTime")
	},
	{
		value: SharePointSearchTimeScope.Last24Hrs,
		text: localize("_Past24hours")
	},
	{
		value: SharePointSearchTimeScope.LastWeek,
		text: localize("_PastWeek")
	},
	{
		value: SharePointSearchTimeScope.LastMonth,
		text: localize("_PastMonth")
	},
	{
		value: SharePointSearchTimeScope.Last3Months,
		text: localize("_Past3Months")
	},
	{
		value: SharePointSearchTimeScope.LastYear,
		text: localize("_PastYear")
	},
	{
		value: SharePointSearchTimeScope.OlderThenAYear,
		text: localize("_OlderThanAYear")
	}
];

const searchInOptions: readonly SelectOption<SharePointSearchValue>[] = [
	{
		value: SharePointSearchValue.All,
		text: localize("_All")
	},
	{
		value: SharePointSearchValue.Title,
		text: localize("_Title")
	}
];

type GlobalContext = {
	// We put sort on GlobalContext instead of the regular sorting field because the result
	// type is composite (i.e. result is not the document but rather document is a field of the result)
	sorting: {
		direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
		propertyName: FileSearchFormData["sorting"]["propertyName"];
	};
	// Trigger new request when searchVersion changes
	searchVersion: Guid;
};

type FilterState = {
	documentKinds: readonly GlobalFileSearchDocumentKind[];
	timeScope: SharePointSearchTimeScope;
	searchIn: SharePointSearchValue;
};

const useDataSource = createRemoteDataSource<
	GlobalFileSearchResultModel,
	FilterState,
	GlobalContext
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<GlobalFileSearchResultModel>> => {
				const {
					searchTerm,
					filterState,
					skip,
					amount,
					signal,
					globalContext: {
						sorting
					}
				} = options;

				const {
					documentKinds,
					timeScope,
					searchIn
				} = filterState;

				if (searchTerm.trim() === "") {
					return {
						hasMore: false,
						items: []
					};
				} else {
					const backendResult: PageResult<GlobalFileSearchResultModel> = await searchFiles(
						{
							searchTerm,
							documentKinds,
							timeScope,
							searchIn,
							skip,
							take: amount,
							order: convertSorting(sorting)
						},
						signal
					);

					const {
						items,
						totalCount
					} = backendResult;

					return {
						items,
						hasMore: items.length >= amount &&
							(skip + items.length) < totalCount
					};
				}
			}
		}
	);

const propertyOrderMap: Record<GlobalContext["sorting"]["propertyName"], SharePointColumnId> = {
	["name"]: SharePointColumnId.name,
	["modifiedOn"]: SharePointColumnId.dateModified
};

function convertSorting(
	sorting: GlobalContext["sorting"]
): SharePointOrderModel | null {
	const property: SharePointColumnId | undefined = propertyOrderMap[sorting.propertyName];
	if (isNil(property)) {
		throw new Error(`Sorting on '${sorting.propertyName}' is not supported`);
	}

	return {
		property,
		direction: convertSortDirectionToDto(sorting.direction)
	};
}

const fileSearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Files
};
